import React, { useState, useMemo, useCallback } from 'react';
import {
  ArrowUpDown, ArrowUp, ArrowDown, Filter, X, Download, FileSpreadsheet,
  ChevronLeft, ChevronRight, Search,
} from 'lucide-react';
import * as XLSX from 'xlsx';

/**
 * DataGrid — BI Phase 1 reusable Excel-like table.
 *
 * Props
 *   - columns: [{
 *       key:        unique id for the column
 *       label:      header text
 *       accessor:   row → raw value (used for sort + filter + export)
 *       render?:    row → JSX (defaults to accessor output)
 *       type?:      'text' | 'number' | 'date' | 'enum' (controls filter UI)
 *       align?:     'left' | 'right' | 'center'
 *       width?:     css width (e.g. 'w-32')
 *       enumOptions?: string[]   // required if type='enum'
 *       sortable?:  default true
 *       filterable?: default true
 *       exportable?: default true     // include in CSV / xlsx
 *     }]
 *   - rows: data array
 *   - initialSort?: { key, dir }      // dir: 'asc' | 'desc'
 *   - pageSize?: default 25; pass 0 to disable pagination
 *   - exportName?: filename prefix for downloads (default 'export')
 *   - testId?: data-testid prefix
 *   - rowKey?: row → string
 *   - emptyText?: string
 *   - title?: header text
 *   - subtitle?: small text under title
 *   - icon?: JSX node for the title icon
 *   - actions?: JSX rendered in the toolbar's right slot
 */
export default function DataGrid({
  columns,
  rows,
  initialSort = null,
  pageSize = 25,
  exportName = 'export',
  testId = 'data-grid',
  rowKey = (r, i) => r.id || r._id || i,
  emptyText = 'No data',
  title = null,
  subtitle = null,
  icon = null,
  actions = null,
}) {
  const [sort, setSort]     = useState(initialSort);          // { key, dir }
  const [filters, setFilters] = useState({});                  // { key: value | {min,max} | string[] }
  const [page, setPage]     = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [search, setSearch] = useState('');

  const visibleCols = useMemo(() => columns.filter(c => c), [columns]);

  // --- FILTERED ROWS ----------------------------------------------------
  const filtered = useMemo(() => {
    const q = (search || '').trim().toLowerCase();
    return (rows || []).filter(row => {
      // Global search runs over all stringified accessor values
      if (q) {
        const haystack = visibleCols
          .map(c => String((c.accessor ? c.accessor(row) : '') ?? '').toLowerCase())
          .join(' ');
        if (!haystack.includes(q)) return false;
      }
      for (const col of visibleCols) {
        const f = filters[col.key];
        if (f === undefined || f === '' || f === null) continue;
        const val = col.accessor ? col.accessor(row) : row[col.key];

        if (col.type === 'number' || col.type === 'date') {
          // Range filter: {min, max}
          const min = f.min !== undefined && f.min !== '' ? (col.type === 'number' ? parseFloat(f.min) : f.min) : null;
          const max = f.max !== undefined && f.max !== '' ? (col.type === 'number' ? parseFloat(f.max) : f.max) : null;
          const v = col.type === 'number' ? parseFloat(val) : val;
          if (min !== null && (Number.isNaN(v) || v < min)) return false;
          if (max !== null && (Number.isNaN(v) || v > max)) return false;
        } else if (col.type === 'enum') {
          // Multi-select array
          if (Array.isArray(f) && f.length > 0 && !f.includes(String(val))) return false;
        } else {
          const needle = String(f).toLowerCase();
          if (!String(val ?? '').toLowerCase().includes(needle)) return false;
        }
      }
      return true;
    });
  }, [rows, visibleCols, filters, search]);

  // --- SORTED ROWS ------------------------------------------------------
  const sorted = useMemo(() => {
    if (!sort || !sort.key) return filtered;
    const col = visibleCols.find(c => c.key === sort.key);
    if (!col) return filtered;
    const get = col.accessor || ((r) => r[sort.key]);
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      let va = get(a); let vb = get(b);
      if (col.type === 'number') {
        va = parseFloat(va); vb = parseFloat(vb);
        if (Number.isNaN(va)) va = -Infinity;
        if (Number.isNaN(vb)) vb = -Infinity;
        return (va - vb) * dir;
      }
      va = (va == null ? '' : String(va)).toLowerCase();
      vb = (vb == null ? '' : String(vb)).toLowerCase();
      return va < vb ? -dir : va > vb ? dir : 0;
    });
  }, [filtered, sort, visibleCols]);

  // --- PAGINATION -------------------------------------------------------
  const total = sorted.length;
  const totalPages = pageSize > 0 ? Math.max(1, Math.ceil(total / pageSize)) : 1;
  const pageRows = pageSize > 0 ? sorted.slice((page - 1) * pageSize, page * pageSize) : sorted;

  // Clamp page when filters shrink rows
  if (page > totalPages && totalPages > 0) setTimeout(() => setPage(totalPages), 0);

  // --- SORT TOGGLE ------------------------------------------------------
  const toggleSort = useCallback((key) => {
    setSort(s => {
      if (!s || s.key !== key) return { key, dir: 'asc' };
      if (s.dir === 'asc') return { key, dir: 'desc' };
      return null;
    });
  }, []);

  const setFilter = useCallback((key, value) => {
    setFilters(f => {
      const next = { ...f };
      if (value === '' || value === null || value === undefined ||
          (Array.isArray(value) && value.length === 0) ||
          (typeof value === 'object' && !Array.isArray(value) && Object.values(value).every(v => v === '' || v === null || v === undefined))) {
        delete next[key];
      } else {
        next[key] = value;
      }
      return next;
    });
    setPage(1);
  }, []);

  const clearFilters = useCallback(() => { setFilters({}); setSearch(''); setPage(1); }, []);

  // --- EXPORT -----------------------------------------------------------
  const exportRows = (extension) => {
    const exportCols = visibleCols.filter(c => c.exportable !== false);
    const data = sorted.map(row => {
      const out = {};
      exportCols.forEach(c => {
        const v = c.accessor ? c.accessor(row) : row[c.key];
        out[c.label] = v == null ? '' : v;
      });
      return out;
    });
    if (extension === 'csv') {
      const header = exportCols.map(c => `"${String(c.label).replace(/"/g, '""')}"`).join(',');
      const lines = data.map(r => exportCols.map(c => {
        const v = r[c.label];
        return `"${String(v).replace(/"/g, '""')}"`;
      }).join(','));
      const blob = new Blob([header + '\n' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${exportName}-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } else {
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
      XLSX.writeFile(wb, `${exportName}-${new Date().toISOString().slice(0, 10)}.xlsx`);
    }
  };

  const filterCount = Object.keys(filters).length;
  const hasAnyFilter = filterCount > 0 || search;

  // --- RENDER -----------------------------------------------------------
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden" data-testid={testId}>
      {(title || actions) && (
        <div className="px-4 py-2.5 border-b border-zinc-800 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            {icon}
            {title && <h3 className="text-zinc-200 text-sm font-semibold">{title}</h3>}
            {subtitle && <span className="text-zinc-500 text-[10px]">{subtitle}</span>}
            <span className="text-zinc-500 text-[10px] ml-1" data-testid={`${testId}-count`}>
              · {total} {total === 1 ? 'row' : 'rows'}
            </span>
          </div>
          <div className="flex items-center gap-1.5">{actions}</div>
        </div>
      )}

      <div className="px-3 py-2 border-b border-zinc-800 flex items-center gap-2 flex-wrap bg-zinc-950/40">
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-600" />
          <input
            value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search all columns…"
            data-testid={`${testId}-search`}
            className="w-full bg-zinc-900 border border-zinc-800 rounded-md pl-7 pr-2 py-1 text-[11px] text-zinc-100 focus:outline-none focus:border-amber-500"
          />
        </div>
        <button
          onClick={() => setShowFilters(s => !s)}
          data-testid={`${testId}-toggle-filters`}
          className={`inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-md border ${showFilters || filterCount ? 'bg-amber-900/30 text-amber-300 border-amber-700/40' : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:text-zinc-200'}`}
        >
          <Filter size={10}/> Filters{filterCount > 0 && ` (${filterCount})`}
        </button>
        {hasAnyFilter && (
          <button onClick={clearFilters} className="text-[10px] text-zinc-400 hover:text-zinc-100 px-1.5" data-testid={`${testId}-clear-filters`}>
            <X size={9} className="inline mr-0.5"/> Clear
          </button>
        )}
        <div className="flex-1"/>
        <button
          onClick={() => exportRows('csv')}
          data-testid={`${testId}-export-csv`}
          title="Download filtered+sorted rows as CSV"
          className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-md border border-zinc-800 bg-zinc-900 text-zinc-400 hover:text-zinc-200">
          <Download size={10}/> CSV
        </button>
        <button
          onClick={() => exportRows('xlsx')}
          data-testid={`${testId}-export-xlsx`}
          title="Download filtered+sorted rows as Excel (.xlsx)"
          className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-md border border-emerald-700/40 bg-emerald-900/30 text-emerald-300 hover:text-emerald-200">
          <FileSpreadsheet size={10}/> Excel
        </button>
      </div>

      {showFilters && (
        <div className="px-3 py-2 border-b border-zinc-800 bg-zinc-950/60 flex items-start gap-2 flex-wrap" data-testid={`${testId}-filter-row`}>
          {visibleCols.filter(c => c.filterable !== false).map(col => (
            <ColFilter
              key={col.key}
              col={col}
              value={filters[col.key]}
              onChange={(v) => setFilter(col.key, v)}
              rows={rows}
              testId={`${testId}-filter-${col.key}`}
            />
          ))}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-zinc-800/40">
            <tr className="text-zinc-500 uppercase tracking-wider text-[10px]">
              {visibleCols.map(col => (
                <th key={col.key} className={`px-3 py-2 ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'} ${col.width || ''}`}>
                  <button
                    type="button"
                    onClick={() => col.sortable !== false && toggleSort(col.key)}
                    className={`inline-flex items-center gap-1 ${col.sortable !== false ? 'hover:text-amber-400 cursor-pointer' : 'cursor-default'}`}
                    data-testid={`${testId}-sort-${col.key}`}
                  >
                    {col.label}
                    {col.sortable !== false && (
                      sort?.key === col.key
                        ? (sort.dir === 'asc' ? <ArrowUp size={9}/> : <ArrowDown size={9}/>)
                        : <ArrowUpDown size={9} className="opacity-30"/>
                    )}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr><td colSpan={visibleCols.length} className="text-center text-zinc-500 py-6 text-xs">{hasAnyFilter ? 'No rows match these filters.' : emptyText}</td></tr>
            ) : pageRows.map((row, idx) => (
              <tr key={rowKey(row, idx)} className="border-b border-zinc-800/40 hover:bg-zinc-800/20" data-testid={`${testId}-row-${idx}`}>
                {visibleCols.map(col => {
                  const cellAlign = col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left';
                  return (
                    <td key={col.key} className={`px-3 py-2 ${cellAlign} ${col.width || ''}`}>
                      {col.render ? col.render(row) : (col.accessor ? col.accessor(row) : row[col.key])}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pageSize > 0 && total > pageSize && (
        <div className="px-3 py-2 border-t border-zinc-800 flex items-center justify-between text-[10px] text-zinc-500">
          <div>
            Showing <span className="text-zinc-300">{(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)}</span> of <span className="text-zinc-300">{total}</span>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              data-testid={`${testId}-prev-page`}
              className="p-1 rounded text-zinc-400 hover:text-amber-400 disabled:opacity-30 disabled:hover:text-zinc-400">
              <ChevronLeft size={11}/>
            </button>
            <span className="text-zinc-400">Page <span className="text-zinc-200 font-mono">{page}</span> / {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              data-testid={`${testId}-next-page`}
              className="p-1 rounded text-zinc-400 hover:text-amber-400 disabled:opacity-30 disabled:hover:text-zinc-400">
              <ChevronRight size={11}/>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ColFilter({ col, value, onChange, rows, testId }) {
  if (col.type === 'number' || col.type === 'date') {
    const v = value || {};
    return (
      <div className="flex items-center gap-1" data-testid={testId}>
        <span className="text-[10px] text-zinc-500">{col.label}:</span>
        <input
          type={col.type === 'date' ? 'date' : 'number'}
          placeholder="min" value={v.min || ''}
          onChange={e => onChange({ ...v, min: e.target.value })}
          className="w-20 bg-zinc-900 border border-zinc-800 rounded px-1.5 py-0.5 text-[10px] text-zinc-100 focus:outline-none focus:border-amber-500"
        />
        <span className="text-zinc-600">–</span>
        <input
          type={col.type === 'date' ? 'date' : 'number'}
          placeholder="max" value={v.max || ''}
          onChange={e => onChange({ ...v, max: e.target.value })}
          className="w-20 bg-zinc-900 border border-zinc-800 rounded px-1.5 py-0.5 text-[10px] text-zinc-100 focus:outline-none focus:border-amber-500"
        />
      </div>
    );
  }
  if (col.type === 'enum') {
    const options = col.enumOptions || Array.from(new Set(
      (rows || []).map(r => col.accessor ? col.accessor(r) : r[col.key]).filter(Boolean).map(String)
    )).sort();
    const selected = Array.isArray(value) ? value : [];
    return (
      <div className="flex items-center gap-1 flex-wrap" data-testid={testId}>
        <span className="text-[10px] text-zinc-500">{col.label}:</span>
        {options.map(opt => (
          <button
            key={opt}
            onClick={() => {
              const next = selected.includes(opt) ? selected.filter(x => x !== opt) : [...selected, opt];
              onChange(next);
            }}
            className={`text-[10px] px-1.5 py-0.5 rounded border ${selected.includes(opt) ? 'bg-amber-500/20 text-amber-200 border-amber-500/40' : 'bg-zinc-900 text-zinc-400 border-zinc-800 hover:border-zinc-700'}`}
            data-testid={`${testId}-opt-${opt}`}
          >
            {opt}
          </button>
        ))}
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1" data-testid={testId}>
      <span className="text-[10px] text-zinc-500">{col.label}:</span>
      <input
        value={value || ''} onChange={e => onChange(e.target.value)}
        placeholder="contains…"
        className="w-32 bg-zinc-900 border border-zinc-800 rounded px-1.5 py-0.5 text-[10px] text-zinc-100 focus:outline-none focus:border-amber-500"
      />
    </div>
  );
}
